import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, Copy, Loader2, Pencil, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { copyToClipboard } from '../../lib/clipboard';
import { errorText } from '../../lib/apiErrors';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
// Mirrors `NEW_CODE_PATTERN` in services/referral_codes.py (#799 QA: `----` was
// accepted). Your own older IDs are exempt, as on the server: they already exist.
const PATTERN = /^[a-z0-9][a-z0-9-]{2,22}[a-z0-9]$/;
const FORMAT_ERROR = 'Use 4–24 lower-case letters, digits and hyphens, starting and ending with a letter or digit.';

/**
 * Settings → Profile → Referral ID (#793 S6).
 *
 * The code partner apps send with a lead to credit this teammate (who then owns
 * the lead). Changing it keeps the old one as an alias that still credits them,
 * so codes already handed out keep working. There are deliberately no per-app
 * sign-up links here (D9): each app decides how people enter the code.
 */
export default function ReferralIdCard({ getAuthHeader }) {
    const navigate = useNavigate();
    const token = getAuthHeader()?.Authorization;
    const headers = useMemo(() => (token ? { Authorization: token } : {}), [token]);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        axios.get(`${API}/users/profile/referral-id`, { headers })
            .then((res) => { if (!cancelled) setData(res.data || null); })
            .catch(() => { if (!cancelled) setData(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [headers]);

    const current = data?.referral_id;
    const aliases = Array.isArray(data?.aliases) ? data.aliases : [];
    const credited = Number.isFinite(data?.credited_last_30_days) ? data.credited_last_30_days : 0;

    const startEdit = () => {
        setDraft(current || '');
        setError('');
        setEditing(true);
    };

    const save = async () => {
        const value = draft.trim().toLowerCase();
        // #799 QA: saving the ID you already have used to report "updated".
        if (value === current) {
            setEditing(false);
            return;
        }
        if (!PATTERN.test(value) && !aliases.includes(value)) {
            setError(FORMAT_ERROR);
            return;
        }
        setSaving(true);
        setError('');
        try {
            const res = await axios.put(`${API}/users/profile/referral-id`, { referral_id: value }, { headers });
            setData(res.data);
            setEditing(false);
            if (res.data?.changed !== false) toast.success('Referral ID updated. Your old ID keeps working.');
        } catch (err) {
            setError(errorText(err, 'Could not change your Referral ID.'));
        } finally {
            setSaving(false);
        }
    };

    const copy = async () => {
        if (await copyToClipboard(current)) toast.success('Referral ID copied');
        else toast.error('Select the ID and copy it manually');
    };

    return (
        <div className="border border-violet-200 rounded-lg p-5 ring-4 ring-violet-50" data-testid="referral-id-card">
            <h3 className="text-sm font-semibold text-stone-900 mb-2 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Share2 className="w-4 h-4 text-stone-500" /> Referral ID
            </h3>
            <p className="text-[13.5px] text-stone-600 leading-relaxed mb-3.5">
                Partner apps send this with a lead to credit you. You become the lead’s owner.
            </p>

            {loading ? (
                <Loader2 className="w-5 h-5 text-stone-400 animate-spin" />
            ) : editing ? (
                <div className="flex flex-col gap-1.5">
                    <div className="flex gap-1.5 max-w-[440px]">
                        <input
                            className={`h-9 flex-1 px-3 border rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/20 ${error ? 'border-red-500' : 'border-stone-200'}`}
                            aria-label="Referral ID"
                            data-testid="referral-id-input"
                            value={draft}
                            maxLength={24}
                            onChange={(e) => setDraft(e.target.value)}
                        />
                        <button type="button" data-testid="referral-id-save" disabled={saving} onClick={save} className="h-9 px-3 rounded-md bg-stone-900 text-white text-[13px] font-medium hover:bg-stone-800 disabled:opacity-50">
                            Save
                        </button>
                        <button type="button" onClick={() => setEditing(false)} className="h-9 px-3 rounded-md border border-stone-200 text-[13px] font-medium hover:bg-stone-50">
                            Cancel
                        </button>
                    </div>
                    <span className={`text-xs ${error ? 'text-red-600' : 'text-stone-500'}`} data-testid="referral-id-hint">
                        {error || 'Lower-case letters, numbers and hyphens, 4–24 characters, starting and ending with a letter or number. Your old ID keeps working.'}
                    </span>
                </div>
            ) : current ? (
                <>
                    <div className="flex flex-wrap items-center gap-2.5">
                        <code className="font-mono text-base font-semibold bg-stone-100 px-3 py-1.5 rounded-md" data-testid="referral-id-value">{current}</code>
                        <button type="button" onClick={copy} className="h-8 px-3 rounded-md border border-stone-200 text-[13px] font-medium inline-flex items-center gap-1.5 hover:bg-stone-50">
                            <Copy className="w-3.5 h-3.5" /> Copy
                        </button>
                        <button type="button" data-testid="referral-id-change" onClick={startEdit} className="h-8 px-3 rounded-md text-[13px] font-medium inline-flex items-center gap-1.5 hover:bg-stone-100">
                            <Pencil className="w-3.5 h-3.5" /> Change
                        </button>
                    </div>
                    {aliases.length > 0 && (
                        <div className="text-xs text-stone-500 mt-2" data-testid="referral-id-aliases">
                            Older IDs that still credit you:{' '}
                            {aliases.map((a) => <span key={a} className="inline-flex font-mono text-[11px] bg-stone-100 text-stone-600 rounded px-1.5 py-0.5 mr-1">{a}</span>)}
                        </div>
                    )}
                </>
            ) : (
                <p className="text-sm text-stone-500">Your Referral ID could not be loaded. Reload the page to try again.</p>
            )}

            <p className="text-xs text-stone-500 mt-3.5 leading-relaxed">
                Give it to any partner app. Each app decides how people enter it, for example a sign-up link, a promo code or a “Who referred you?” question.
            </p>
            <div className="mt-4 pt-3.5 border-t border-stone-100 flex items-center gap-2.5">
                <span className="text-[13.5px]" data-testid="referral-id-credited">
                    <b>{credited} {credited === 1 ? 'lead' : 'leads'}</b> credited to you in the last 30 days
                </span>
                <span className="flex-1" />
                <button type="button" onClick={() => navigate('/contacts?referred_by=me')} className="h-8 px-3 rounded-md border border-stone-200 text-[13px] font-medium inline-flex items-center gap-1.5 hover:bg-stone-50" data-testid="referral-id-view-contacts">
                    View in Contacts <ArrowRight className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );
}
